import { useState, useEffect, useRef, useCallback } from "react";

/* ─── GOOGLE FONT INJECT ─────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("cardo-font")) {
  const l = document.createElement("link");
  l.id = "cardo-font"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Cardo:ital,wght@0,400;0,700;1,400&display=swap";
  document.head.appendChild(l);
}

const CARDO = "'Cardo', Georgia, serif";

/* ─── DATA ───────────────────────────────────────────────────── */
const INDUSTRIES = [
  {
    title: "Construction",
    desc: "Monitor site safety in real time, manage compliance checklists and reduce incident risk across every floor.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M17 6V4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2" /><path d="M12 12v4" /><path d="M10 14h4" /></svg>,
  },
  {
    title: "Oil & Gas",
    desc: "Enforce zero-tolerance safety protocols, track hazardous zones and keep your crew audit-ready at all times.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v7" /><path d="M5 9h14" /><path d="M5 9a7 7 0 0 0 14 0" /><path d="M5 9v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" /><path d="M9 21v-6a3 3 0 0 1 6 0v6" /></svg>,
  },
  {
    title: "Pharma & F&B",
    desc: "Stay ahead of regulatory audits, automate batch compliance and eliminate contamination blind spots instantly.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path d="m8.5 8.5 7 7" /></svg>,
  },
  {
    title: "Manufacturing",
    desc: "Detect safety violations on the production line, enforce PPE compliance and cut downtime from incidents.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20" /><path d="M6 20V10l6-8 6 8v10" /><path d="M10 20v-5h4v5" /></svg>,
  },
  {
    title: "Warehouse & Logistics",
    desc: "Track forklift zones, enforce aisle safety rules and ensure every shift meets compliance standards hands-free.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z" /><path d="M6 18h12" /><path d="M6 14h12" /><path d="M10 22V10l4-1 4 1v12" /></svg>,
  },
  {
    title: "Energy & Utilities",
    desc: "Protect high-voltage environments, automate safety inspections and maintain regulatory compliance without gaps.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></svg>,
  },
];

const N_IND = INDUSTRIES.length;
const SEG_DEG = 360 / N_IND;
const INIT_ROT = 180;
const toRad = (d) => (d * Math.PI) / 180;

function segPath(cx, cy, r0, r1, a0, a1) {
  const s = toRad(a0), e = toRad(a1);
  const pt = (r, a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  const [x1, y1] = pt(r1, s), [x2, y2] = pt(r1, e);
  const [x3, y3] = pt(r0, e), [x4, y4] = pt(r0, s);
  return `M${x1} ${y1}A${r1} ${r1} 0 0 1 ${x2} ${y2}L${x3} ${y3}A${r0} ${r0} 0 0 0 ${x4} ${y4}Z`;
}

function getLabelPos(i, rotation, VW, VH, CX, CY, INNER, OUTER) {
  const rotated = toRad(i * SEG_DEG + rotation);
  const r = (INNER + OUTER) / 2;
  return {
    leftPct: ((CX + r * Math.sin(rotated)) / VW) * 100,
    topPct: ((CY - r * Math.cos(rotated)) / VH) * 100,
  };
}

export default function CinematicWheel() {
  const [rotation, setRotation] = useState(INIT_ROT);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const rotRef = useRef(INIT_ROT);
  const rafRef = useRef(null);
  const autoRef = useRef(null); // NEW

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const animateTo = useCallback((target) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = rotRef.current;
    let diff = target - start;
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    const dur = 700, t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const ease = p < .5 ? 2 * p * p : -1 + (4 - 2 * p) * p;
      rotRef.current = start + diff * ease;
      setRotation(rotRef.current);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const getTargetRot = (i) => INIT_ROT + i * SEG_DEG;

  const goNext = useCallback(() => {
    setActiveIdx((prevIdx) => {
      const next = (prevIdx + 1) % N_IND;
      animateTo(getTargetRot(next));
      return next;
    });
  }, [animateTo]);

  const goPrev = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    const prev = (activeIdx - 1 + N_IND) % N_IND;
    setActiveIdx(prev);
    animateTo(getTargetRot(prev));
    startAutoScroll(); // restart timer after manual nav
  };

  const goNextManual = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    goNext();
    startAutoScroll();
  };

  const startAutoScroll = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      goNext();
    }, 5000);
  }, [goNext]);

  // Auto-scroll only on mobile
  useEffect(() => {
    if (isMobile) {
      startAutoScroll();
    } else if (autoRef.current) {
      clearInterval(autoRef.current);
    }
    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [isMobile, startAutoScroll]);

  if (isMobile) {
    return <MobileView rotation={rotation} activeIdx={activeIdx} goNext={goNextManual} goPrev={goPrev} />;
  }
  return <DesktopView rotation={rotation} activeIdx={activeIdx} goNext={goNext} goPrev={goPrev} />;
}

/* ═══════════════════════════════════════════════════════════
   DESKTOP VIEW — white/light bg, full wheel
═══════════════════════════════════════════════════════════ */
function DesktopView({ rotation, activeIdx, goNext, goPrev }) {
  const VW = 1000, VH = 520, CX = 500, CY = -135, INNER = 240, OUTER = 790;

  return (
    <section style={{
      position: "relative", width: "100%", minHeight: "100vh",
      overflow: "hidden", fontFamily: CARDO,
      /* White top fading to deep black bottom — smooth gradient */
      background: "linear-gradient(180deg,#ffffff 0%,#f3eeff 8%,#e8dcff 16%,#c4a8f5 24%,#9060e0 34%,#5a20b0 44%,#2d0d70 56%,#150438 72%,#06010f 88%,#000000 100%)",
    }}>
      {/* Radial blobs — subtle on top, strong purple mid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 28% 18%, rgba(63,24,188,0.18), transparent 30%),
          radial-gradient(ellipse at 70% 25%, rgba(180,40,200,0.12), transparent 28%),
          radial-gradient(ellipse at 50% 55%, rgba(80,20,200,0.25), transparent 45%)
        `}} />
      {/* Bottom fade to pure black */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
        background: "linear-gradient(to top,#000 0%,rgba(0,0,0,0.70) 50%,transparent 100%)",
        pointerEvents: "none"
      }} />

      {/* Wheel container */}
      <div style={{
        position: "absolute", left: "50%", top: "4px",
        width: "clamp(900px,82vw,1180px)", height: "clamp(490px,44vw,614px)",
        transform: "translateX(-50%)",
      }}>
        <WheelCore rotation={rotation} VW={VW} VH={VH} CX={CX} CY={CY} INNER={INNER} OUTER={OUTER}
          hubTop="-310px" hubW="600px" hubH="610px"
          logoTop="72px" logoFontSize="52px"
          labelW="210px" iconSize={84} iconInner={36}
          titleSize="1.18rem" descSize="0.88rem" isLight={false} />
      </div>

      {/* Tagline */}
      <div style={{
        position: "absolute",
        top: "clamp(490px,44vw,614px)",
        left: "50%", transform: "translateX(-50%)",
        textAlign: "center", zIndex: 8, pointerEvents: "none",
        width: "min(65vw,680px)", paddingTop: 28,
      }}>
        <p style={{
          fontSize: "clamp(14px,1.15vw,18px)", lineHeight: 1.75,
          color: "rgba(200,170,255,0.75)", fontStyle: "italic", fontFamily: CARDO, fontWeight: 700, margin: 0,
        }}>
          Built for high-risk, high-compliance floors — if your site depends on safety and can't afford a blind spot, TruVue AI fits in.
        </p>
      </div>

      {/* Nav */}
      <div style={{
        position: "absolute", bottom: "clamp(18px,3vh,36px)", left: 0, right: 0, zIndex: 20,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "0 clamp(16px,3vw,40px)",
      }}>
        <NavBtn onClick={goPrev} primary={false}>← Prev</NavBtn>
        <NavBtn onClick={goNext} primary>Next →</NavBtn>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   MOBILE VIEW — dark theme, wheel top-half, content card below
   Exactly matching the reference screenshot
═══════════════════════════════════════════════════════════ */
function MobileView({ rotation, activeIdx, goNext, goPrev }) {
  const VW = 1000, VH = 520, CX = 500, CY = -135, INNER = 240, OUTER = 790;
  const MOB_SCALE = 0.34;
  const logicW = 1180, logicH = 614;
  const contW = logicW * MOB_SCALE;
  const contH = logicH * MOB_SCALE;
  const ind = INDUSTRIES[activeIdx];

  return (
    <section style={{
      position: "relative", width: "100%",
      /* No minHeight — fits content exactly, no empty space */
      overflow: "hidden", fontFamily: CARDO,
      background: "linear-gradient(180deg,#1a0840 0%,#130630 35%,#0a0420 65%,#050210 100%)",
      display: "flex", flexDirection: "column", alignItems: "center",
      paddingBottom: 28,
    }}>

      {/* Radial bg glows */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 30% 18%, rgba(80,20,220,0.70), transparent 38%),
          radial-gradient(ellipse at 68% 28%, rgba(190,30,200,0.45), transparent 32%),
          radial-gradient(ellipse at 50% 55%, rgba(60,10,160,0.30), transparent 50%)
        `}} />

      {/* Smooth gradient bridge between wheel and card */}
      <div style={{
        position: "absolute",
        top: `${contH - 60}px`,
        left: 0, right: 0, height: "220px",
        background: "linear-gradient(to bottom,transparent 0%,rgba(10,4,32,0.85) 60%,rgba(5,2,16,0.97) 100%)",
        pointerEvents: "none", zIndex: 2,
      }} />

      {/* ── WHEEL (scaled, flush top) ── */}
      <div style={{
        position: "relative", zIndex: 3,
        width: `${contW}px`, height: `${contH}px`,
        flexShrink: 0, marginTop: 0,
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: `${logicW}px`, height: `${logicH}px`,
          transform: `scale(${MOB_SCALE})`, transformOrigin: "top left",
        }}>
          <WheelCore
            rotation={rotation}
            VW={VW} VH={VH} CX={CX} CY={CY} INNER={INNER} OUTER={OUTER}
            hubTop="-310px" hubW="600px" hubH="610px"
            logoTop="72px" logoFontSize="52px"
            labelW="210px" iconSize={84} iconInner={36}
            titleSize="1.05rem" descSize="0.78rem"
            isLight={false}
          />
        </div>
      </div>

      {/* ── CONTENT CARD — clearly visible below wheel ── */}
      <div style={{
        position: "relative", zIndex: 4,
        width: "calc(100% - 40px)", maxWidth: "360px",
        marginTop: 12,
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(177,92,255,0.18)",
        borderRadius: 20,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        padding: "24px 22px 22px",
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center",
        boxShadow: "0 8px 40px rgba(0,0,0,0.45),inset 0 1px 0 rgba(255,255,255,0.08)",
      }}>
        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 14,
          border: "1px solid rgba(177,92,255,0.24)",
          background: "linear-gradient(135deg,rgba(63,24,188,0.60) 0%,rgba(7,3,15,0.90) 100%)",
          boxShadow: "inset 0 0 20px rgba(177,92,255,0.16),0 0 28px rgba(140,70,255,0.28)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          <span style={{
            display: "flex", width: 28, height: 28,
            color: "#b15cff",
            filter: "drop-shadow(0 0 8px rgba(177,92,255,0.88))",
          }}>{ind.icon}</span>
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: "1.25rem", fontWeight: 700,
          letterSpacing: "-0.01em", lineHeight: 1.2,
          color: "#fff", margin: "0 0 10px", fontFamily: CARDO,
        }}>{ind.title}</h3>

        {/* Accent bar */}
        <div style={{
          height: 2, width: 40, borderRadius: 2,
          background: "linear-gradient(to right,#ffb066,#e879f9,transparent)",
          marginBottom: 12,
        }} />

        {/* Description */}
        <p style={{
          fontSize: "0.92rem", lineHeight: 1.65,
          color: "rgba(220,200,255,0.80)",
          margin: "0 0 4px", fontFamily: CARDO,
        }}>{ind.desc}</p>
      </div>

      {/* Tagline */}
      <div style={{
        position: "relative", zIndex: 4,
        width: "calc(100% - 48px)", maxWidth: "340px",
        marginTop: 18, textAlign: "center",
      }}>
        <p style={{
          fontSize: "0.82rem", lineHeight: 1.7,
          color: "rgba(180,150,255,0.65)",
          fontStyle: "italic", fontFamily: CARDO, fontWeight: 700, margin: 0,
        }}>
          Built for high-risk, high-compliance floors — if your site depends on safety and can't afford a blind spot, TruVue AI fits in.
        </p>
      </div>

      {/* Dots */}
      <div style={{
        position: "relative", zIndex: 4,
        display: "flex", gap: 6, alignItems: "center",
        marginTop: 20,
      }}>
        {INDUSTRIES.map((_, i) => (
          <div key={i} style={{
            width: i === activeIdx ? 18 : 5, height: 5, borderRadius: 3,
            background: i === activeIdx ? "#b15cff" : "rgba(177,92,255,0.25)",
            transition: "all 0.3s",
          }} />
        ))}
      </div>

      {/* ── NAV — inline, right below dots ── */}
      <div style={{
        position: "relative", zIndex: 4,
        width: "calc(100% - 40px)", maxWidth: "360px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 20,
      }}>
        <button onClick={goPrev} style={mobBtn(false)}>← Prev</button>
        <button onClick={goNext} style={mobBtn(true)}>Next →</button>
      </div>

    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHARED WHEEL CORE
═══════════════════════════════════════════════════════════ */
function WheelCore({
  rotation, VW, VH, CX, CY, INNER, OUTER,
  hubTop, hubW, hubH, logoTop, logoFontSize,
  labelW, iconSize, iconInner, titleSize, descSize,
  isLight,
}) {
  const labelColor = isLight ? "#1e1442" : "#fff";
  const labelDescClr = isLight ? "rgba(74,69,96,0.85)" : "rgba(255,255,255,0.60)";
  const iconBg = isLight
    ? "linear-gradient(135deg,#fff 0%,#f3effc 100%)"
    : "linear-gradient(135deg,rgba(63,24,188,0.55) 0%,rgba(7,3,15,0.88) 100%)";
  const iconColor = isLight ? "#7c3aed" : "#b15cff";
  const iconShadow = isLight
    ? "inset 0 0 18px rgba(177,92,255,0.06),0 4px 20px rgba(120,80,200,0.18)"
    : "inset 0 0 20px rgba(177,92,255,0.14),0 0 28px rgba(140,70,255,0.28)";

  return (
    <>
      {/* Glow blob under wheel */}
      <div style={{
        position: "absolute", bottom: "6%", left: "12%", right: "12%", height: 160,
        borderRadius: "50%",
        background: isLight ? "rgba(100,60,210,0.10)" : "rgba(109,40,217,0.22)",
        filter: "blur(65px)", pointerEvents: "none",
      }} />

      {/* ── SVG WHEEL ── */}
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          overflow: "visible",
          transform: `rotate(${rotation}deg)`,
          transformOrigin: `${(CX / VW) * 100}% ${(CY / VH) * 100}%`,
          willChange: "transform",
        }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="wBase" x1="0%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor="#1a0845" stopOpacity=".82" />
            <stop offset="100%" stopColor="#0d0430" stopOpacity=".70" />
          </linearGradient>
          <linearGradient id="wBleed" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c828d2" stopOpacity=".16" />
            <stop offset="50%" stopColor="#3f18bc" stopOpacity=".20" />
            <stop offset="100%" stopColor="#1a0a3d" stopOpacity=".05" />
          </linearGradient>
          <linearGradient id="wLight" x1="0%" y1="0%" x2="55%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity=".18" />
            <stop offset="30%" stopColor="#c0a0ff" stopOpacity=".09" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity=".01" />
          </linearGradient>
          <linearGradient id="wRim" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity=".28" />
            <stop offset="40%" stopColor="#c828d2" stopOpacity=".32" />
            <stop offset="100%" stopColor="#3f18bc" stopOpacity=".14" />
          </linearGradient>
          <radialGradient id="wGlow" cx="50%" cy="25%" r="65%">
            <stop offset="0%" stopColor="#a070ff" stopOpacity=".22" />
            <stop offset="100%" stopColor="#3f18bc" stopOpacity=".00" />
          </radialGradient>
        </defs>

        {INDUSTRIES.map((_, i) => {
          const s = i * SEG_DEG - SEG_DEG / 2;
          const e = s + SEG_DEG;
          const d = segPath(CX, CY, INNER, OUTER, s, e);
          return (
            <g key={i}>
              <path d={d} fill="url(#wBase)" />
              <path d={d} fill="url(#wBleed)" />
              <path d={d} fill="url(#wLight)" />
              <path d={d} fill="url(#wGlow)" />
              <path d={d} fill="none" stroke="#130540" strokeWidth="3.5" strokeLinejoin="round" />
              <path d={d} fill="none" stroke="url(#wRim)" strokeOpacity=".82" strokeWidth=".85" />
              <path d={d} fill="none" stroke="#ffffff" strokeOpacity=".055" strokeWidth=".45" />
            </g>
          );
        })}
      </svg>

      {/* ── UPRIGHT LABELS ── */}
      {INDUSTRIES.map((ind, i) => {
        const { leftPct, topPct } = getLabelPos(i, rotation, VW, VH, CX, CY, INNER, OUTER);
        return (
          <div key={i} style={{
            position: "absolute",
            left: `${leftPct}%`, top: `${topPct}%`,
            transform: "translate(-50%,-50%)",
            width: labelW, pointerEvents: "none", zIndex: 5,
          }}>
            <div style={{
              display: "flex", flexDirection: "column",
              alignItems: "center", textAlign: "center",
              filter: "drop-shadow(0 10px 26px rgba(0,0,0,0.65))",
            }}>
              <div style={{
                width: iconSize, height: iconSize, borderRadius: 14,
                border: "1px solid rgba(177,92,255,0.24)",
                background: iconBg, boxShadow: iconShadow,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 11, flexShrink: 0,
              }}>
                <span style={{
                  display: "flex", width: iconInner, height: iconInner,
                  color: iconColor,
                  filter: isLight ? "none" : "drop-shadow(0 0 9px rgba(177,92,255,0.88))",
                }}>{ind.icon}</span>
              </div>
              <h3 style={{
                fontSize: titleSize, fontWeight: 700,
                letterSpacing: "-0.01em", lineHeight: 1.2,
                color: labelColor, margin: "0 0 6px", fontFamily: CARDO,
              }}>{ind.title}</h3>
              <div style={{
                height: 2, width: 34, borderRadius: 2,
                background: "linear-gradient(to right,#ffb066,#e879f9,transparent)",
                marginBottom: 7,
              }} />
              <p style={{
                fontSize: descSize, lineHeight: 1.5,
                color: labelDescClr, margin: 0, fontFamily: CARDO,
              }}>{ind.desc}</p>
            </div>
          </div>
        );
      })}

      {/* ── HUB ── */}
      <div style={{
        position: "absolute", left: "50%", top: hubTop,
        width: hubW, height: hubH,
        transform: "translateX(-50%)",
        borderRadius: "50%",
        background: "linear-gradient(180deg,#ffffff 0%,#ede0ff 10%,#c4a0f0 22%,#8040d0 36%,#4a18a0 50%,#220860 65%,#0d0330 82%,#000000 100%)",
        boxShadow: "0 20px 80px rgba(0,0,0,0.90),0 0 60px rgba(80,20,200,0.20),inset 0 0 50px rgba(60,20,160,0.10)",
        outline: "1px solid rgba(160,100,255,0.18)",
        pointerEvents: "none", zIndex: 6,
      }} />

      {/* ── LOGO ── */}
      <div style={{
        position: "absolute", left: "50%", top: logoTop,
        transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", zIndex: 7, pointerEvents: "none", whiteSpace: "nowrap",
      }}>
        <p style={{
          fontSize: "9px", letterSpacing: "0.32em", fontWeight: 400,
          color: "rgba(255,255,255,0.40)", textTransform: "uppercase",
          fontFamily: CARDO, margin: "0 0 14px",
        }}>Built for every industry</p>
        <div style={{
          fontSize: logoFontSize, fontWeight: 700,
          letterSpacing: "-2px", lineHeight: 1,
          color: "#fff", fontFamily: CARDO,
        }}>TruVue AI</div>
        <p style={{
          marginTop: "20px", fontSize: "9px", letterSpacing: "0.22em",
          color: "rgba(255,255,255,0.36)", textTransform: "uppercase", fontFamily: CARDO,
        }}>Safety & Compliance Platform</p>
      </div>
    </>
  );
}

/* ─── NAV BUTTONS ────────────────────────────────────────────── */
function NavBtn({ children, onClick, primary }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 6,
      padding: "11px 26px", borderRadius: 999,
      background: primary ? "rgba(177,92,255,0.12)" : "rgba(255,255,255,0.06)",
      border: primary ? "1.5px solid rgba(177,92,255,0.45)" : "1px solid rgba(255,255,255,0.15)",
      color: primary ? "rgba(210,170,255,0.95)" : "rgba(255,255,255,0.72)",
      fontSize: "clamp(12px,0.95vw,15px)", fontWeight: 700,
      cursor: "pointer", fontFamily: CARDO,
      letterSpacing: "0.04em", backdropFilter: "blur(6px)",
      transition: "all 0.2s",
    }}>{children}</button>
  );
}

const mobBtn = (primary) => ({
  display: "flex", alignItems: "center", gap: 6,
  padding: "10px 22px", borderRadius: 999,
  background: primary ? "rgba(177,92,255,0.12)" : "rgba(255,255,255,0.06)",
  border: primary ? "1.5px solid rgba(177,92,255,0.45)" : "1px solid rgba(255,255,255,0.15)",
  color: primary ? "rgba(210,170,255,0.95)" : "rgba(255,255,255,0.70)",
  fontSize: 13, fontWeight: 700,
  cursor: "pointer", fontFamily: CARDO,
  letterSpacing: "0.04em", backdropFilter: "blur(8px)",
});